import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiButtonGroup,
  MiIcon,
  MiModal,
  MiModalConfirm,
  MiPageHeader,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
  PERMISSIONS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Slide, Typography } from "@mui/material";
import {
  INVOICING_ROUTES,
  RECONCILIATION_STEPS,
} from "../../accountingConstants";
import { useReconcileContext } from "../ReconcileContext";
import {
  BACKCHARGES_TAB,
  RECONCILE_DOCUMENTS_TABS,
} from "../reconcileConstants";
import { AddPurchaseOrderModal } from "./AddPurchaseOrderModal/AddPurchaseOrderModal";
import { BackchargeDetails } from "./Backcharge/BackchargeDetails";
import { BackchargesList } from "./Backcharge/BackchargesList";
import { CreateBackchargeModal } from "./Backcharge/CreateBackchargeModal";
import { EmptyPurchaseOrders } from "./EmptyPurchaseOrders/EmptyPurchaseOrders";
import { InvoiceDisplay } from "./InvoiceDisplay/InvoiceDisplay";
import { PurchaseOrderDetails } from "./PurchaseOrderDetails/PurchaseOrderDetails";
import { PurchaseOrderList } from "./PurchaseOrderList/PurchaseOrderList";
import { useReconcileDocumentsSectionViewModel } from "./useReconcileDocumentsSectionViewModel";

const getChipColor = (openBalance) => {
  if (openBalance > 0) {
    return "success";
  }
  if (openBalance < 0) {
    return "error";
  }
  return "grey";
};

const BACKCHARGES_TAB_STATE = {
  id: BACKCHARGES_TAB,
  value: BACKCHARGES_TAB,
};

export const ReconcileDocumentsSection = memo(() => {
  const {
    order = {},
    activeStep,
    purchaseOrders,
    orderTemplates,
    getPurchaseOrders,
    invoices,
    invoicesModule,
    fetchAndEnsureDraftInvoice,
    isInvoiceMode,
    viewMode,
    updateInvoiceFormState,
    clearItemVerification,
  } = useReconcileContext();
  const recentlyAddedPurchaseOrder = useRef(null);
  const recentlyCreatedBackcharge = useRef(null);
  const pendingPurchaseOrderId = useRef(null);

  const { hasPermission } = usePermissions();

  const { classes, theme } = useStyles();
  const [activeTab, setActiveTab] = useState(
    RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS
  );

  const [activeOrderTemplateTab, setActiveOrderTemplateTab] = useState(null);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState(null);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [activePurchaseOrder, setActivePurchaseOrder] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [isPurchaseOrderModalOpen, setIsPurchaseOrderModalOpen] =
    useState(false);
  const [isBackchargeModalOpen, setIsBackchargeModalOpen] = useState(false);
  const [activeBackcharge, setActiveBackcharge] = useState(null);
  const [selectedBackcharge, setSelectedBackcharge] = useState(null);
  const [isVoidBackchargeModalOpen, setIsVoidBackchargeModalOpen] =
    useState(false);

  const clearPurchaseOrderFormState = (purchaseOrderId) => {
    invoices.forEach((invoice) => {
      (invoice.invoiceBillingCommitments || [])
        .filter((ibc) => ibc.billingCommitmentId === purchaseOrderId)
        .forEach((ibc) => {
          const key = ibc.billingCommitmentId + invoice.id;
          updateInvoiceFormState(key, null);
          clearItemVerification(key);
        });
    });
  };

  const {
    isLoading,
    deletePurchaseOrder,
    voidPurchaseOrder,
    getBackcharges,
    voidBackcharge,
    updateBackcharge,
    createBackcharge,
    backcharges,
  } = useReconcileDocumentsSectionViewModel({
    onDelete: () => {
      clearPurchaseOrderFormState(pendingPurchaseOrderId.current);
      pendingPurchaseOrderId.current = null;
      getPurchaseOrders();
      fetchAndEnsureDraftInvoice();
      setIsDeleteModalOpen(false);
      setActivePurchaseOrder(null);
      setSelectedPurchaseOrder(null);
    },
    onRefresh: () => {
      clearPurchaseOrderFormState(pendingPurchaseOrderId.current);
      pendingPurchaseOrderId.current = null;
      getPurchaseOrders();
      fetchAndEnsureDraftInvoice();
    },
  });

  const orderTemplateTabs = useMemo(
    () =>
      orderTemplates
        .map((orderTemplate) => {
          const orderTemplatePurchaseOrders =
            purchaseOrders?.filter(
              ({ orderTemplateId }) => orderTemplate.id === orderTemplateId
            ) || [];

          const [openBalance, amount] = orderTemplatePurchaseOrders
            .filter(({ voidDate }) => !voidDate)
            .reduce(
              (acc, { openBalance, amount }) => [
                acc[0] + openBalance,
                acc[1] + amount,
              ],
              [0, 0]
            );

          const samePoWithOrderTemplate =
            orderTemplate.id === order?.orderTemplateId;

          return {
            ...orderTemplate,
            label: samePoWithOrderTemplate ? (
              <Box display="flex" alignItems="center" gap={1}>
                <MiIcon
                  path={mdi.mdiLink}
                  size={0.85}
                  color={theme.palette.cyan.dark}
                />
                <Typography fontSize={14} fontWeight={600}>
                  {orderTemplate.name}
                </Typography>
              </Box>
            ) : (
              orderTemplate.name
            ),
            value: orderTemplate.id,
            chipLabel: "",
            amount,
            openBalance,
            chipColor: getChipColor(openBalance),
            purchaseOrders: orderTemplatePurchaseOrders,
          };
        })
        .filter(({ purchaseOrders }) => purchaseOrders.length !== 0),

    [orderTemplates, purchaseOrders]
  );

  const invoicesTabs = useMemo(
    () =>
      invoices.map(({ id, name, ...rest }) => ({
        label: name,
        value: id,
        ...rest,
      })),
    [invoices]
  );

  const canCreatePO = hasPermission(PERMISSIONS.BILLING_COMMITMENT.CREATE);
  const canCreateCreditMemos = hasPermission(PERMISSIONS.CREDIT_MEMO.CREATE);
  const isReviewMode = activeStep === RECONCILIATION_STEPS.REVIEW_SUBMIT;
  const isPreBillMode = viewMode === INVOICING_ROUTES.PRE_BILL;
  const hasBackcharges = backcharges.length > 0;
  const hasOrderTemplateTabs = orderTemplateTabs.length > 0;
  const isDocumentsTabActive =
    activeTab === RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS ||
    activeTab === RECONCILE_DOCUMENTS_TABS.BACKCHARGES;
  const isBackchargesTemplateTab =
    activeOrderTemplateTab?.id === BACKCHARGES_TAB;
  const shouldShowDocumentActions =
    isDocumentsTabActive && canCreatePO && !isReviewMode;
  const currentDocumentTab = isBackchargesTemplateTab
    ? BACKCHARGES_TAB
    : activeOrderTemplateTab?.id;
  const secondaryDocumentTabs = [
    ...orderTemplateTabs,
    ...(hasBackcharges
      ? [
          {
            ...BACKCHARGES_TAB_STATE,
            label: "Backcharges",
          },
        ]
      : []),
  ];
  const shouldShowDocumentTabs =
    secondaryDocumentTabs.length > 0 && isDocumentsTabActive;

  const activatePurchaseOrdersTab = useCallback((orderTemplateTab = null) => {
    setActiveTab(RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS);
    setActiveOrderTemplateTab(orderTemplateTab);
  }, []);

  const activateBackchargesTab = useCallback(() => {
    setActiveTab(RECONCILE_DOCUMENTS_TABS.BACKCHARGES);
    setActiveOrderTemplateTab(BACKCHARGES_TAB_STATE);
  }, []);

  const handleTabChange = useCallback(
    (tab) => {
      activatePurchaseOrdersTab(
        orderTemplateTabs.find(({ id }) => id === tab) || null
      );
      setActivePurchaseOrder(null);
    },
    [activatePurchaseOrdersTab, orderTemplateTabs]
  );

  const handlePurchaseOrderDetailsOpen = useCallback((purchaseOrder) => {
    setActivePurchaseOrder(purchaseOrder);
  }, []);

  const handlePurchaseOrderDetailsClose = useCallback(() => {
    setActivePurchaseOrder(null);
  }, []);

  const handleDeleteModalOpen = useCallback((purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsDeleteModalOpen(true);
  }, []);
  const handleVoidModalOpen = useCallback((purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsVoidModalOpen(true);
  }, []);

  const handleVoidModalClose = useCallback(() => {
    setIsVoidModalOpen(false);
    setSelectedPurchaseOrder(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedPurchaseOrder(null);
  }, []);

  const handleOpenCreateOrEditModal = useCallback((purchaseOrder) => {
    if (purchaseOrder) {
      setSelectedPurchaseOrder(purchaseOrder);
    }
    setIsPurchaseOrderModalOpen(true);
  }, []);

  const handlePurchaseOrderModalClose = useCallback(() => {
    setIsPurchaseOrderModalOpen(false);
    setSelectedPurchaseOrder(null);
  }, []);

  const refreshBackcharges = useCallback(() => {
    if (order?.homeId) {
      getBackcharges({
        homeId: order.homeId,
        accountId: order?.accountId || order?.account?.id,
      });
    }
  }, [order?.homeId, order?.accountId, order?.account?.id]);

  const handleBackchargeClick = useCallback((backcharge) => {
    setActiveBackcharge(backcharge);
  }, []);

  const handleBackchargeBack = useCallback(() => {
    setActiveBackcharge(null);
  }, []);

  const handleBackchargeEdit = useCallback((backcharge) => {
    setSelectedBackcharge(backcharge);
    setIsBackchargeModalOpen(true);
  }, []);

  const handleBackchargeDelete = useCallback((backcharge) => {
    setSelectedBackcharge(backcharge);
    setIsVoidBackchargeModalOpen(true);
  }, []);

  const handleVoidBackcharge = useCallback(async () => {
    await voidBackcharge({
      id: selectedBackcharge.id,
      modifiedOn: selectedBackcharge.modifiedOn,
    });
    setIsVoidBackchargeModalOpen(false);
    setSelectedBackcharge(null);
    setActiveBackcharge(null);
    refreshBackcharges();
  }, [refreshBackcharges, selectedBackcharge, voidBackcharge]);

  const handleBackchargeSubmit = useCallback(
    async (data) => {
      if (selectedBackcharge) {
        await updateBackcharge({
          id: selectedBackcharge.id,
          data,
          modifiedOn: selectedBackcharge.modifiedOn,
        });
        setIsBackchargeModalOpen(false);
        setSelectedBackcharge(null);
        setActiveBackcharge(null);
        refreshBackcharges();
        return;
      }
      const response = await createBackcharge({
        accountId: order?.accountId || order?.account?.id,
        homeId: order?.homeId,
        ...data,
      });
      setIsBackchargeModalOpen(false);
      recentlyCreatedBackcharge.current = response?.data || true;
      refreshBackcharges();
    },
    [
      selectedBackcharge,
      order?.accountId,
      order?.account?.id,
      order?.homeId,
      refreshBackcharges,
    ]
  );

  const renderTab = () => {
    if (activeTab === RECONCILE_DOCUMENTS_TABS.INVOICES) {
      if (!activeInvoiceTab) {
        return null;
      }
      return <InvoiceDisplay invoice={activeInvoiceTab} />;
    }
    if (activeTab === RECONCILE_DOCUMENTS_TABS.BACKCHARGES) {
      return activeBackcharge ? (
        <BackchargeDetails
          backcharge={activeBackcharge}
          onBack={handleBackchargeBack}
          onEdit={handleBackchargeEdit}
          onDelete={handleBackchargeDelete}
        />
      ) : (
        <BackchargesList
          backcharges={backcharges}
          onClick={handleBackchargeClick}
          onEdit={handleBackchargeEdit}
          onDelete={handleBackchargeDelete}
        />
      );
    }
    if (!activeOrderTemplateTab || isBackchargesTemplateTab) {
      return (
        <EmptyPurchaseOrders
          onAddPurchaseOrder={() => handleOpenCreateOrEditModal()}
        />
      );
    }
    return activePurchaseOrder ? (
      <PurchaseOrderDetails
        viewMode={viewMode}
        isReviewMode={isReviewMode}
        purchaseOrder={activePurchaseOrder}
        onEdit={handleOpenCreateOrEditModal}
        onDelete={handleDeleteModalOpen}
        onVoid={handleVoidModalOpen}
        onBack={handlePurchaseOrderDetailsClose}
      />
    ) : (
      <PurchaseOrderList
        viewMode={viewMode}
        isReviewMode={isReviewMode}
        orderTemplate={activeOrderTemplateTab}
        order={order}
        onClick={handlePurchaseOrderDetailsOpen}
        onEdit={handleOpenCreateOrEditModal}
        onVoid={handleVoidModalOpen}
        onDelete={handleDeleteModalOpen}
      />
    );
  };

  useEffect(() => {
    if (order?.homeId) {
      refreshBackcharges();
    }
  }, [order?.homeId, refreshBackcharges]);

  useEffect(() => {
    if (
      !hasBackcharges &&
      activeTab === RECONCILE_DOCUMENTS_TABS.BACKCHARGES &&
      !recentlyCreatedBackcharge.current
    ) {
      activatePurchaseOrdersTab(
        hasOrderTemplateTabs ? orderTemplateTabs[0] : null
      );
    }
    if (
      hasBackcharges &&
      !hasOrderTemplateTabs &&
      activeTab === RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS
    ) {
      activateBackchargesTab();
    }
    if (recentlyCreatedBackcharge.current && hasBackcharges) {
      activateBackchargesTab();
      const created = recentlyCreatedBackcharge.current;
      if (created && typeof created === "object") {
        const match = backcharges.find((bc) => bc.id === created.id);
        setActiveBackcharge(match || created);
      }
      recentlyCreatedBackcharge.current = null;
    }
  }, [
    activateBackchargesTab,
    activatePurchaseOrdersTab,
    activeTab,
    backcharges,
    hasBackcharges,
    hasOrderTemplateTabs,
    orderTemplateTabs,
  ]);

  useEffect(() => {
    if (recentlyAddedPurchaseOrder.current) {
      activatePurchaseOrdersTab(
        orderTemplateTabs.find(
          ({ id }) => id === recentlyAddedPurchaseOrder.current.orderTemplateId
        ) || null
      );
      setActivePurchaseOrder(
        purchaseOrders.find(
          ({ id }) => id === recentlyAddedPurchaseOrder.current.id
        ) || null
      );
      recentlyAddedPurchaseOrder.current = null;
    } else if (
      hasOrderTemplateTabs &&
      (!activeOrderTemplateTab ||
        (activeOrderTemplateTab.id !== BACKCHARGES_TAB &&
          !orderTemplateTabs.some(
            ({ value }) => value === activeOrderTemplateTab.value
          )))
    ) {
      setActiveOrderTemplateTab(orderTemplateTabs[0]);
    }
  }, [
    activatePurchaseOrdersTab,
    activeOrderTemplateTab,
    hasOrderTemplateTabs,
    orderTemplateTabs,
    purchaseOrders,
  ]);

  useEffect(() => {
    setActiveOrderTemplateTab((prev) => {
      if (prev?.id === BACKCHARGES_TAB) {
        return prev;
      }
      return orderTemplateTabs.find(({ id }) => id === prev?.id) || null;
    });
    setActivePurchaseOrder(
      (prev) => purchaseOrders.find(({ id }) => id === prev?.id) || null
    );
  }, [orderTemplateTabs]);

  useEffect(() => {
    if (isInvoiceMode || viewMode === INVOICING_ROUTES.INVOICED) {
      setActiveInvoiceTab(
        (prev) =>
          invoicesTabs.find(({ value }) => value === prev?.value) ||
          invoicesTabs[0]
      );
      invoicesModule.getOrderInvoicePdfs();
    }
  }, [isInvoiceMode, invoicesTabs]);

  useEffect(() => {
    if (activeStep === RECONCILIATION_STEPS.REVIEW_SUBMIT) {
      setActiveTab(RECONCILE_DOCUMENTS_TABS.INVOICES);
      setActiveInvoiceTab(
        (prev) =>
          invoicesTabs.find(({ value }) => value === prev?.value) ||
          invoicesTabs[0]
      );
      return;
    }

    if (
      activeStep === RECONCILIATION_STEPS.RECONCILIATION &&
      activeTab === RECONCILE_DOCUMENTS_TABS.INVOICES
    ) {
      if (hasBackcharges && !hasOrderTemplateTabs) {
        activateBackchargesTab();
      } else {
        activatePurchaseOrdersTab(
          hasOrderTemplateTabs ? orderTemplateTabs[0] : null
        );
      }
    }
  }, [
    activateBackchargesTab,
    activatePurchaseOrdersTab,
    activeStep,
    activeTab,
    hasBackcharges,
    hasOrderTemplateTabs,
    invoicesTabs,
    orderTemplateTabs,
  ]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box className={classes.headerContainer}>
        <MiPageHeader
          title="Documents"
          rightArea={
            <Box display="flex" gap={2}>
              <Box
                sx={{
                  transition: "transform 200ms ease-in-out",
                  transform:
                    (activeTab === RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS ||
                      activeTab === RECONCILE_DOCUMENTS_TABS.BACKCHARGES) &&
                    shouldShowDocumentActions
                      ? "translateX(0)"
                      : "translateX(160px)",
                }}
              >
                {activeStep !== RECONCILIATION_STEPS.RECONCILIATION ? (
                  <MiButtonGroup
                    selected={activeTab}
                    data={[
                      {
                        label: "Payment Information",
                        value: RECONCILE_DOCUMENTS_TABS.PURCHASE_ORDERS,
                      },
                      {
                        label: "Invoices",
                        value: RECONCILE_DOCUMENTS_TABS.INVOICES,
                      },
                    ]}
                    handleClick={(item) => setActiveTab(item.value)}
                  />
                ) : null}
              </Box>
              {canCreatePO && (
                <Slide direction="left" in={shouldShowDocumentActions}>
                  <Box display="flex" gap={1}>
                    {isPreBillMode && (
                      <MiButton
                        title="Backcharge"
                        startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                        variant={BUTTON_VARIANTS.OUTLINED}
                        disabled={!canCreateCreditMemos}
                        onClick={() => setIsBackchargeModalOpen(true)}
                      />
                    )}
                    <MiButton
                      title="Payment Information"
                      startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                      variant={BUTTON_VARIANTS.OUTLINED}
                      onClick={() => handleOpenCreateOrEditModal()}
                    />
                  </Box>
                </Slide>
              )}
            </Box>
          }
        />
        {shouldShowDocumentTabs && (
          <MiTab
            isSecondary
            currentTab={currentDocumentTab}
            tabs={secondaryDocumentTabs}
            chipStyle={{ height: 10, width: 10 }}
            onTabChange={(tab) => {
              if (tab === BACKCHARGES_TAB) {
                activateBackchargesTab();
              } else {
                handleTabChange(tab);
              }
            }}
          />
        )}
        {invoicesTabs.length > 0 &&
          activeTab === RECONCILE_DOCUMENTS_TABS.INVOICES && (
            <MiTab
              isSecondary
              currentTab={activeInvoiceTab?.value}
              tabs={invoicesTabs}
              onTabChange={(tab) =>
                setActiveInvoiceTab(
                  invoicesTabs.find(({ value }) => value === tab)
                )
              }
            />
          )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: 0,
          padding: theme.spacing(2, 4, 0, 4),
        }}
      >
        {renderTab()}
      </Box>
      <AddPurchaseOrderModal
        open={isPurchaseOrderModalOpen}
        homeId={order?.homeId}
        selectedPurchaseOrder={selectedPurchaseOrder}
        orderTemplates={orderTemplates}
        onCreate={(po) => {
          recentlyAddedPurchaseOrder.current = po;
        }}
        onClose={handlePurchaseOrderModalClose}
        onPurchaseOrdersRefresh={getPurchaseOrders}
      />
      <CreateBackchargeModal
        open={isBackchargeModalOpen}
        isLoading={isLoading}
        selectedBackcharge={selectedBackcharge}
        onClose={() => {
          setIsBackchargeModalOpen(false);
          setSelectedBackcharge(null);
        }}
        onSubmit={handleBackchargeSubmit}
      />
      <MiModalConfirm
        title="Void Backcharge"
        open={isVoidBackchargeModalOpen}
        disableClick={isLoading}
        titleIcon={{
          path: mdi[ICONS_WEB.CANCEL],
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.VOID}
        buttonPositiveColor={BUTTON_COLORS.ERROR}
        loading={isLoading}
        handleNegative={() => {
          setIsVoidBackchargeModalOpen(false);
          setSelectedBackcharge(null);
        }}
        handlePositive={handleVoidBackcharge}
        description={`Are you sure you want to void the backcharge "#${selectedBackcharge?.poNumber}"?`}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={isDeleteModalOpen}
        disableClick={isLoading}
        titleIcon={{
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={handleDeleteModalClose}
        handlePositive={() => {
          pendingPurchaseOrderId.current = selectedPurchaseOrder?.id;
          deletePurchaseOrder(selectedPurchaseOrder);
        }}
        description={`Are you sure you want to delete the payment information "${selectedPurchaseOrder?.name}"?`}
      />
      <MiModal
        open={isVoidModalOpen}
        closeModal={handleVoidModalClose}
        width={400}
        titleIcon={mdi[ICONS_WEB.CANCEL]}
        leftIconColor={theme.palette.red.dark}
        title="Void Payment Information"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: handleVoidModalClose,
          },
          {
            name: "Void PO",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.RED,
            disabled: isLoading,
            onClick: () => {
              pendingPurchaseOrderId.current = selectedPurchaseOrder?.id;
              voidPurchaseOrder({
                id: selectedPurchaseOrder.id,
                modifiedOn: selectedPurchaseOrder.modifiedOn,
              });
              handleVoidModalClose();
            },
          },
        ]}
      >
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          color="grey"
          mb={2}
          px={2}
          fontSize={theme.fontSize.medium}
        >
          Voiding payment information will also void invoices which have been
          linked to that payment information.
        </Typography>
      </MiModal>
    </Box>
  );
});

const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
  },
  activeHeaderTabButton: {
    backgroundColor: `${theme.palette.background.paper} !important`,
    color: `${theme.palette.common.black} !important`,
    fontWeight: theme.fontWeight.bold,
  },
  fieldDescription: {
    maxWidth: 110,
    fontWeight: theme.fontWeight.bold,
  },
}));
